import { Divider, ListItemText, MenuItem } from '@mui/material';
import { selectDefaultCreationCrmOrgId } from '../../../../reducers/userInfoReducer';
import { useSelector } from 'react-redux';
import { selectConnectedCrmOrgs } from '../environmentsReducer';
import { Fragment, useCallback } from 'react';
import { Button, colors } from '@sweep-io/sweep-design';
import { StyledListItem } from '../../../common/StyledListItem';
import { CheckCircle, Clock, Warning } from '@sweep-io/sweep-design/dist/icons';
import { useChangeEnvironment } from '../../../../hooks/useChangeEnvironment';
import WithConnectOrgPermission from '../../../common/WithConnectOrgPermission';
import EnvFetchProgressBar from '../../../dashboard/fetch-progress-bar/EnvFetchProgressBar';
import { FetchStatus } from '@server/fetch_status.type';
import { CrmOrg } from '../../../../types/CrmOrgs';
import { useFeatureToggle } from '../../../../feature-flags/useFeatureToggle';
import { appRoutes } from '../../../../constants/appRoutes';
import { useNavigate } from 'react-router';
import { UserSettingsTab } from '../../../dashboard/user-settings/types';

interface EnvMenuItemsProps {
  onConnectProdClick: () => void;
  onManageEnvClick: () => void;
  close: () => void;
  onChangeEnvCallback?: () => void;
  disableManageEnv?: boolean;
  allowedEnvIds?: string[];
}

export const EnvMenuItems = ({
  onConnectProdClick,
  onManageEnvClick,
  close,
  onChangeEnvCallback,
  disableManageEnv,
  allowedEnvIds,
}: EnvMenuItemsProps) => {
  const navigate = useNavigate();
  const { environmentsPage, multiAccounts } = useFeatureToggle();
  const defaultCreationCrmOrgId = useSelector(selectDefaultCreationCrmOrgId);
  const environments = useSelector(selectConnectedCrmOrgs);
  const { changeEnvironment } = useChangeEnvironment();

  const [prodEnvs, sandboxEnvs] = environments.reduce(
    (acc, curr) => {
      curr.isMain ? acc[0].push(curr) : acc[1].push(curr);
      return acc;
    },
    [[] as CrmOrg[], [] as CrmOrg[]],
  );
  sandboxEnvs.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  const isProdConnected = prodEnvs?.length > 0;
  const isAnyOrgFetching = environments.some(
    (env) =>
      env.fetchStatus === FetchStatus.Fetching || env.fetchStatus === FetchStatus.InitialFetching,
  );
  const itemsWidth = isAnyOrgFetching || !isProdConnected ? '330px' : '240px';

  const setDefaultCreationEnvironmentHandler = useCallback(
    (envId: string) => async () => {
      changeEnvironment(envId);
      close();
      onChangeEnvCallback && onChangeEnvCallback();
    },
    [changeEnvironment, close, onChangeEnvCallback],
  );

  const renderEnvItem = ({
    title,
    subtitle,
    onClick,
    EndAdornment,
    StartIcon,
    isActiveEnv,
  }: {
    title: string;
    subtitle: string;
    onClick?: () => void;
    EndAdornment: React.ReactNode;
    StartIcon: React.ReactNode;
    isActiveEnv?: boolean;
  }) => {
    return (
      <>
        <StyledListItem
          StartIcon={StartIcon}
          title={title}
          subtitle={subtitle}
          onClick={onClick}
          sx={{
            cursor: onClick ? 'pointer' : 'default',
            maxHeight: '56px',
            mb: 0,
            p: '8px 24px 8px 16px',
            background: isActiveEnv ? colors.blue[100] : 'transparent',
            '&:hover': {
              background: isActiveEnv && onClick ? colors.blue[100] : undefined,
            },
            width: itemsWidth,
            overflow: 'hidden',
          }}
          EndAdornment={EndAdornment}
        />
        <Divider sx={{ borderColor: colors.grey[120] }} />
      </>
    );
  };

  const connectProdItem = () => {
    if (prodEnvs?.length > 0) {
      return null;
    }
    return (
      <>
        {renderEnvItem({
          title: 'Production',
          subtitle: 'Not Connected',
          EndAdornment: (
            <WithConnectOrgPermission>
              <Button size="tiny" onClick={onConnectProdClick}>
                Connect
              </Button>
            </WithConnectOrgPermission>
          ),
          StartIcon: <Warning />,
        })}
      </>
    );
  };
  const envs: CrmOrg[] = [...prodEnvs, ...sandboxEnvs].filter((env) =>
    allowedEnvIds ? allowedEnvIds.includes(env.id) : true,
  );

  const renderItem = (env: CrmOrg) => {
    const isActiveEnv = defaultCreationCrmOrgId === env.id;
    const envType = env.isMain ? 'Production' : 'Sandbox';

    const isError =
      env.fetchStatus === FetchStatus.Error || env.fetchStatus === FetchStatus.InvalidCredentials;
    const isFetching =
      env.fetchStatus === FetchStatus.Fetching || env.fetchStatus === FetchStatus.InitialFetching;

    const icons = {
      error: <Warning size="medium" />,
      fetching: <Clock size="medium" />,
      connected: <CheckCircle size="medium" />,
    };

    let icon = icons.connected;
    if (isError) {
      icon = icons.error;
    } else if (isFetching) {
      icon = icons.fetching;
    }

    return renderEnvItem({
      title: env.name,
      subtitle: envType,
      onClick: setDefaultCreationEnvironmentHandler(env.id),
      EndAdornment: <EnvFetchProgressBar crmOrg={env} />,
      StartIcon: icon,
      isActiveEnv,
    });
  };

  return (
    <>
      {connectProdItem()}
      {envs.map((env) => (
        <Fragment key={env.id}>{renderItem(env)}</Fragment>
      ))}
      {!disableManageEnv && (
        <MenuItem
          onClick={
            environmentsPage
              ? () => {
                  if (multiAccounts) {
                    navigate(appRoutes.settings.route + `?tab=${UserSettingsTab.ENVIRONMENTS}`);
                    close();
                    return;
                  }
                  navigate(appRoutes.environments.route);
                  close();
                }
              : onManageEnvClick
          }
          sx={{
            p: '8px 24px 8px 16px',
            mt: 1,
          }}
        >
          <ListItemText sx={{ color: colors.blue[500] }}>Manage Environments</ListItemText>
        </MenuItem>
      )}
    </>
  );
};
